/**
 * Cora — the CV screening assistant — as the Cora page sees it: runs, quality numbers, operations.
 * Every call goes to /api/cora/*, which the proxy gates on the Candidate Communication permission.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export type CoraMode = 'off' | 'shadow' | 'live';
export type Recommendation = 'SHORTLIST RECOMMENDATION' | 'CONSIDER WITH CLARIFICATIONS' | 'MISSING MUST HAVES';
export type RunStatus = 'running' | 'screened' | 'no_resume' | 'failed' | 'write_failed';
export type Outcome = 'advanced' | 'archived' | 'pending' | 'unknown';
export type CriterionStatus = 'met' | 'missing' | 'unsure';

export interface CoraDocsGrant {
  googleEmail: string;
  connectedBy: string;
  connectedAt: string | null;
  lastError: string | null;
  canCreateFiles: boolean;
}

export interface CoraStatus {
  mode: CoraMode;
  model: string;
  promptVersion: string;
  performAsConfigured: boolean;
  docsConfigured: boolean;
}

export interface CoraRunSummary {
  id: string;
  mode: 'shadow' | 'live';
  status: RunStatus;
  opportunityId: string;
  candidateName: string;
  positionId: string;
  positionTitle: string;
  recruiter: string;
  recommendation: Recommendation | null;
  salaryMismatch: boolean;
  baselineRecommendation: Recommendation | null;
  ruleCheckAgrees: boolean | null;
  outcome: Outcome;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
  hasFeedback: boolean;
}

type OutcomeCounts = Record<Outcome, number>;

export interface CoraStats {
  total: number;
  statuses: Partial<Record<RunStatus, number>>;
  recommendations: Record<Recommendation, number>;
  salaryMismatch: number;
  ruleDisagreements: number;
  feedbackDisagree: number;
  costUsd: number;
  avgDurationMs: number | null;
  outcomes: Record<Recommendation, OutcomeCounts>;
  shadow: {
    compared: number;
    agree: number;
    awaitingBaseline: number;
    noOldVerdict: number;
    agreementPct: number | null;
    pairs: Record<string, number>;
  };
}

export interface Criterion {
  criterion: string;
  status: CriterionStatus;
  evidence: 'strong' | 'moderate' | 'weak' | 'none';
  qualifier: string;
  detail: string;
}

export interface CoraFeedback {
  verdict: 'agree' | 'disagree';
  comment: string;
  by: string;
  at: string;
}

export interface CoraRun extends Omit<CoraRunSummary, 'outcome' | 'baselineRecommendation' | 'ruleCheckAgrees' | 'hasFeedback'> {
  postingId: string;
  noResumeReason: string | null;
  resumeSource: 'pdf' | 'image' | 'docx' | 'lever-parse' | 'none';
  assessment: null | {
    cvSignalQuality: 'sufficient' | 'weak' | 'negative';
    mustHaves: Criterion[];
    niceToHaves: Criterion[];
    salary: { status: CriterionStatus; note: string };
    quickChecklist: string[];
  };
  ruleCheck: { implied: Recommendation; agrees: boolean } | null;
  note: string;
  tags: string[];
  writes?: { note: string; tags: string };
  baseline?: { recommendation: Recommendation | ''; text: string; createdAt: string };
  outcome?: { stageId: string; archivedReason: string; archivedAt: string };
  feedback?: CoraFeedback[];
  model: string | null;
  promptVersion: string;
  costUsd: number;
  durationMs?: number;
  attempts?: number;
  rerunBy?: string;
  triggeredBy?: string;
}

export interface CoraPosition {
  positionId: string;
  title: string;
  postingId: string;
  recruiter: string;
}

export interface CoraTick {
  id: string;
  at: string;
  mode: CoraMode;
  positions: number;
  candidatesFound: number;
  skippedByTag: number;
  alreadyHandled: number;
  deferred: number;
  byStatus: Partial<Record<RunStatus | 'already-handled', number>>;
  issues: { positionId: string; title: string; issue: string; detail?: string }[];
  durationMs: number;
}

/** An API refusal; `code` is set when the page can act on it (e.g. `needs-confirmation`). */
export class CoraApiError extends Error {
  readonly code: string | null;
  constructor(message: string, code: string | null) {
    super(message);
    this.name = 'CoraApiError';
    this.code = code;
  }
}

/**
 * Statuses the Cora routes answer with a reason the page shows (or acts on, like 409
 * `needs-confirmation`). authenticatedFetch throws on any other non-OK status before the body is
 * read, so without this list the page would only ever see "Request failed with status 409".
 */
const HANDLED_STATUSES = [400, 403, 404, 409, 422, 502, 503];

/** The proxy's reason: `message` for RBAC refusals, `error` for Cora's own. */
const reasonOf = (body: { error?: string; message?: string }, status: number) =>
  body.message || body.error || `HTTP ${status}`;

async function getJson<T>(path: string): Promise<T> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/cora${path}`, { suppressErrorStatuses: HANDLED_STATUSES });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new CoraApiError(reasonOf(body, res.status), body.code ?? null);
  return body as T;
}

async function postJson<T>(path: string, payload: unknown): Promise<T> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/cora${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    suppressErrorStatuses: HANDLED_STATUSES,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new CoraApiError(reasonOf(body, res.status), body.code ?? null);
  return body as T;
}

export const fetchCoraStatus = () => getJson<CoraStatus>('/status');
export const fetchCoraRuns = (days: number) =>
  getJson<{ days: number; stats: CoraStats; runs: CoraRunSummary[] }>(`/runs?days=${days}`);
export const fetchCoraRun = (id: string) => getJson<{ run: CoraRun }>(`/runs/${encodeURIComponent(id)}`);
export const fetchCoraTicks = () => getJson<{ ticks: CoraTick[] }>('/ticks');
export const submitCoraFeedback = (id: string, verdict: 'agree' | 'disagree', comment: string) =>
  postJson<{ feedback: CoraFeedback[] }>(`/runs/${encodeURIComponent(id)}/feedback`, { verdict, comment });
/** `expectedMode`: the mode the page showed; the server refuses (409 `mode-changed`) if it moved on. */
export const rerunCora = (id: string, expectedMode: CoraMode, confirmDuplicateNote = false) =>
  postJson<{ status: string }>(`/runs/${encodeURIComponent(id)}/rerun`, { confirmDuplicateNote, expectedMode });
export const fetchCoraPositions = () => getJson<{ positions: CoraPosition[] }>('/positions');
/** Screen one candidate now. `candidate` is a Lever link or opportunity ID. */
export const screenCoraCandidate = (candidate: string, expectedMode: CoraMode, positionId?: string, confirmDuplicateNote = false) =>
  postJson<{ status: string; id: string; mode: 'shadow' | 'live' }>('/screen', { candidate, positionId, confirmDuplicateNote, expectedMode });
export const fetchCoraDocsConnection = () => getJson<{ available: boolean; grant: CoraDocsGrant | null }>('/docs-connection');
/** Starts Google's consent; the caller sends the browser to the returned URL. */
export const connectCoraDocs = () => postJson<{ authorizeUrl: string }>('/docs-connection/connect', {});
export const disconnectCoraDocs = () => postJson<{ grant: null }>('/docs-connection/disconnect', {});

export type KikiStatus = 'running' | 'drafted' | 'published' | 'failed';
export interface KikiRun {
  id: string;
  mode: 'shadow' | 'live';
  status: KikiStatus;
  positionId: string;
  title: string;
  recruiterName: string;
  docUrl: string | null;
  dm: string | null;
  error: string | null;
  triggeredBy: string;
  startedAt: string;
  finishedAt: string | null;
}
export interface KikiRunDetail extends KikiRun {
  scorecard?: { mustHaves: string[]; niceToHaves: string[] };
  text?: string;
  profileFound?: boolean;
  model?: string;
  promptVersion?: string;
}
export interface KikiOverview {
  runs: KikiRun[];
  waiting: { positionId: string; title: string; recruiterName: string }[] | null;
  issues: { positionId: string; title: string; issue: string; detail?: string }[];
}
export const fetchScorecards = () => getJson<KikiOverview>('/scorecards');
export const fetchScorecardRun = (id: string) => getJson<{ run: KikiRunDetail }>(`/scorecards/${encodeURIComponent(id)}`);
export const generateScorecardNow = (positionId: string, expectedMode: CoraMode) =>
  postJson<{ status: string; id: string; mode: 'shadow' | 'live'; dm?: string | null }>(`/scorecards/${encodeURIComponent(positionId)}/generate`, { expectedMode });
